'use client';
import React from 'react';

import { SearchResultCard } from '@/slw/widgets/search-results/search-result-card.ui';

interface SearchResultGridProps {
  listings: any[];
  loading: boolean;
  error: any;
  onRetry?: () => void;
}

function SkeletonCard() {
  return (
    <div className='animate-pulse h-64 bg-gray-100 rounded-2xl flex flex-col gap-3 p-4'>
      <div className='bg-gray-200 h-32 w-full rounded-lg mb-4' />
      <div className='h-4 bg-gray-200 rounded w-2/3 mb-2' />
      <div className='h-4 bg-gray-200 rounded w-1/3 mb-2' />
      <div className='h-4 bg-gray-200 rounded w-1/2' />
    </div>
  );
}

export function SearchResultGrid({ listings, loading, error, onRetry }: SearchResultGridProps) {
  if (loading) {
    return (
      <div className='bg-white rounded-2xl p-6 min-h-[400px] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8'>
        {[...Array(9)].map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className='bg-white rounded-2xl p-8 min-h-[200px] flex flex-col items-center justify-center text-center'>
        <div className='text-lg font-semibold text-[#D32F2F] mb-2'>Something went wrong</div>
        <div className='text-gray-500 mb-4'>We couldn&apos;t load the listings. Please try again.</div>
        {onRetry && (
          <button
            type='button'
            onClick={onRetry}
            className={[
              'bg-[#C9A876] text-white font-semibold px-6 h-12 rounded-lg shadow',
              'hover:bg-[#b08b5c]',
              'focus:outline-none focus:ring-2 focus:ring-[#C9A876]',
              'transition-colors duration-200',
            ].join(' ')}
            aria-label='Retry loading listings'
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!listings || listings.length === 0) {
    return (
      <div className='bg-white rounded-2xl p-8 min-h-[200px] flex flex-col items-center justify-center text-center'>
        <div className='text-lg font-semibold mb-2'>No results found</div>
        <div className='text-gray-500'>Try adjusting your filters to see more vehicles.</div>
      </div>
    );
  }

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 xl:gap-6'>
      {listings.map((listing, idx) => (
        <SearchResultCard key={`${listing.id}_${idx}`} listing={listing} />
      ))}
    </div>
  );
}
